import {Component, OnInit} from '@angular/core';
import {AlertService, GroupService, OperatorService, UserService} from "../services";
import { FormBuilder, FormGroup, Validators } from '@angular/forms';

import { Router, ActivatedRoute } from '@angular/router';
import { first } from 'rxjs/operators';
import {ProductService} from "../services/product.service";
import {CategoryService} from "../services/category.service";
import {MatDialog, MatDialogRef} from "@angular/material/dialog";
import {CategoryDialogComponent} from "../category/dialog/categoryDialog.component";
import {VatRateService} from "../services/vatRate.service";
import {UnitService} from "../services/unit.services";
import {ManufacturerDialogComponent} from "../manufacturer/dialog/manufacturerDialog.component";
import {ManufacturerService} from "../services/manufacturer.service";

import { locale as polish } from './i18n/pl';
import { locale as english } from './i18n/en';
import {FuseTranslationLoaderService} from "../../../@fuse/services/translation-loader.service";
import {ComponentTitleService} from "../services/componentTitle.service";

@Component({
    templateUrl: './productForm.component.html',
    styleUrls: ['./productForm.component.scss']
})

export class ProductFormComponent implements OnInit {

    form: FormGroup;
    loading = false;
    submitted = false;
    id: string;
    isAddMode: boolean;
    categories: any[];
    manufacturers: any[];
    vatRates: any[];
    units: any[];
    dialogRef: any;
    images: any[];

    constructor(
        public _matDialog: MatDialog,
        private formBuilder: FormBuilder,
        private route: ActivatedRoute,
        private router: Router,
        private productService: ProductService,
        private alertService: AlertService,
        private categoryService: CategoryService,
        private vatRateService: VatRateService,
        private unitService: UnitService,
        private manufacturerService: ManufacturerService,
        private translationLoader: FuseTranslationLoaderService,
        private componentTitleService: ComponentTitleService
    ) {
        this.translationLoader.loadTranslations(polish, english);

        componentTitleService.clearTitle();
        componentTitleService.addPart('Products');
    }

    ngOnInit() {
        this.categoryService.getAllByType('product').subscribe((res: any[]) => {
            this.categories = res;
        }, error => {
            this.alertService.error(error);
        });

        this.vatRateService.getAll().subscribe((res: any[]) => {
            this.vatRates = res;
        }, error => {

        });

        this.unitService.getAll().subscribe((res: any[]) => {
            this.units = res;
        }, error => {

        });

        this.manufacturerService.getAll().subscribe((res: any[]) => {
            this.manufacturers = res;
        }, error => {

        });

        this.id = this.route.snapshot.params['id'];
        this.isAddMode = !this.id;

        if (this.isAddMode) {
            this.componentTitleService.addPart('New');
        } else {
            this.componentTitleService.addPart('Edit');
        }

        this.form = this.formBuilder.group({
            name: ['', Validators.required],
            nameForReceipt: ['', Validators.required],
            description: [''],
            category: [''],
            vatRate: [''],
            manufacturer: [''],
            material: [''],
            product: [''],
            type: [''],
            minimalStock: [''],
            priceNet: [''],
            priceGross: [''],
            purchasePriceNet: [''],
            purchasePriceGross: [''],
            barcode: [''],
            unit: [''],
            images: [''],
        });

        if (!this.isAddMode) {
            this.productService.getById(this.id)
                .pipe(first())
                .subscribe(x => {
                    this.f.name.setValue(x.name);
                    this.f.nameForReceipt.setValue(x.nameForReceipt);
                    this.f.description.setValue(x.description);

                    if (x.category) {
                        this.f.category.setValue(x.category.id);
                    }

                    if (typeof x.vatRate !== "undefined" && x.vatRate !== null) {
                        this.f.vatRate.setValue(x.vatRate.id);
                    }

                    if (x.manufacturer) {
                        this.f.manufacturer.setValue(x.manufacturer.id);
                    }
                    this.f.material.setValue(x.material);
                    this.f.product.setValue(x.product);
                    this.f.type.setValue(x.type);
                    this.f.minimalStock.setValue(x.minimalStock);
                    this.f.priceNet.setValue(x.priceNet);
                    this.f.priceGross.setValue(x.priceGross);
                    this.f.purchasePriceGross.setValue(x.purchasePriceGross);
                    this.f.purchasePriceNet.setValue(x.purchasePriceNet);
                    this.f.barcode.setValue(x.barcode);
                    if (x.unit) {
                        this.f.unit.setValue(x.unit.id);
                    }
                });
        }
    }

    // convenience getter for easy access to form fields
    get f() { return this.form.controls; }

    onSubmit() {
        this.submitted = true;

        // reset alerts on submit
        this.alertService.clear();

        // stop here if form is invalid
        if (this.form.invalid) {
            return;
        }

        this.loading = true;

        if (this.isAddMode) {
            this.createProduct();
        } else {
            this.updateProduct();
        }
    }

    public addCategory() {
        this.dialogRef = this._matDialog.open(CategoryDialogComponent, {
            panelClass: 'contact-form-dialog',
            data      : {
                action : 'new',
                categories: this.categories,
            }
        });

        this.dialogRef.afterClosed()
            .subscribe((response: FormGroup) => {
                if ( !response )
                {
                    return;
                }

                let category = response.getRawValue();
                category.type = 'product';

                this.categoryService.create(category)
                    .pipe(first())
                    .subscribe(
                        data => {
                            this.alertService.success('Category added successfully', { keepAfterRouteChange: true });
                        },
                        error => {
                            this.alertService.error(error);
                            this.loading = false;
                        });

            }, (err) => {
                console.log(err);
            });
    }

    public addManufacturer() {
        this.dialogRef = this._matDialog.open(ManufacturerDialogComponent, {
            panelClass: 'manufacturer-form-dialog',
            data      : {
                action : 'new',
            }
        });

        this.dialogRef.afterClosed()
            .subscribe((response: FormGroup) => {
                if ( !response )
                {
                    return;
                }

                let manufacturer = response.getRawValue();

                this.manufacturerService.create(manufacturer)
                    .pipe(first())
                    .subscribe(
                        data => {
                            this.alertService.success('Manufacturer added successfully', { keepAfterRouteChange: true });
                        },
                        error => {
                            this.alertService.error(error);
                            this.loading = false;
                        });

            }, (err) => {
                console.log(err);
            });
    }

    private createProduct() {
        let product = this.form.value;
        product.images = this.images;
        this.productService.create(product)
            .pipe(first())
            .subscribe(
                data => {
                    this.alertService.success('Product added successfully', { keepAfterRouteChange: true });
                    this.router.navigate(['.', { relativeTo: this.route }]);
                },
                error => {
                    this.alertService.error(error);
                    this.loading = false;
                });
    }

    private updateProduct() {
        let product = this.form.value;
        product.images = this.images;
        this.productService.update(this.id, product)
            .pipe(first())
            .subscribe(
                data => {
                    this.alertService.success('Update successful', { keepAfterRouteChange: true });
                    this.router.navigate(['..', { relativeTo: this.route }]);
                },
                error => {
                    this.alertService.error(error);
                    this.loading = false;
                });
    }

    onFileChange(event) {
        this.images=[];
        if(event.target.files && event.target.files.length) {
            const files = event.target.files;
            let a = Array.from(files);

            for (let i=0; i<files.length; i++) {
                let reader = new FileReader();

                reader.readAsDataURL(files[i]);

                reader.onload = () => {
                    this.images.push({
                        "content": reader.result,
                        "name": files[i].name,
                        "size": files[i].size,
                        "type": files[i].type,
                        "objectReference": "",
                        "referenceId": ""
                    });
                };
            }
        }
    }
}
